/**
 * Status token helpers — one visual register for report/memo statuses.
 *
 * Maps domain statuses (IC memo sections, memo lifecycle, research jobs) onto
 * five semantic kinds, each rendered with the Pencil status tokens defined in
 * index.css (`--color-success/-warning/-info/-error`, light + dark). Use these
 * instead of hardcoding `bg-emerald-100 text-emerald-700` etc., which break
 * dark mode and fragment the visual language.
 */

export type StatusKind = 'neutral' | 'progress' | 'success' | 'warning' | 'danger';

/** Badge surface: token background + token foreground. */
export const STATUS_BADGE_CLASSES: Record<StatusKind, string> = {
  neutral: 'bg-secondary text-muted-foreground',
  progress: 'bg-status-info text-status-info-foreground',
  success: 'bg-status-success text-status-success-foreground',
  warning: 'bg-status-warning text-status-warning-foreground',
  danger: 'bg-status-error text-status-error-foreground',
};

/** Small indicator dot: strong foreground tone, readable in both modes. */
export const STATUS_DOT_CLASSES: Record<StatusKind, string> = {
  neutral: 'bg-border',
  progress: 'bg-status-info-foreground',
  success: 'bg-status-success-foreground',
  warning: 'bg-status-warning-foreground',
  danger: 'bg-status-error-foreground',
};

/** IC memo section statuses (plus job-level aliases seen in production data). */
const MEMO_SECTION_STATUS_KIND: Record<string, StatusKind> = {
  not_started: 'neutral',
  pending: 'neutral',
  in_progress: 'progress',
  generated: 'success',
  complete: 'success',
  completed: 'success',
  needs_review: 'warning',
  approved: 'success',
  failed: 'danger',
  error: 'danger',
};

export function memoSectionStatusKind(status: unknown): StatusKind {
  if (typeof status !== 'string') return 'neutral';
  return MEMO_SECTION_STATUS_KIND[status] ?? 'neutral';
}

/**
 * Collapse a section status to the simple state the IC-memo UI shows:
 * Complete / Incomplete / Failed (plus a transient "Generating…"). We key off
 * the section STATUS (which is reliably set), never a `completeness` percentage
 * (which the managed-agent path leaves at 0).
 */
export function memoSectionStatusText(status: unknown): string {
  switch (memoSectionStatusKind(status)) {
    case 'success':
      return 'Complete';
    case 'warning':
      return 'Complete'; // generated content that is merely flagged for review
    case 'danger':
      return 'Failed';
    case 'progress':
      return 'Generating…';
    default:
      return 'Incomplete';
  }
}

/**
 * Roll a memo's section statuses up to one overall status + kind:
 *   any failed → Failed; every section complete → Complete;
 *   any still generating → Generating…; otherwise Incomplete.
 */
export function memoOverallStatus(sectionStatuses: unknown[]): { label: string; kind: StatusKind } {
  if (sectionStatuses.length === 0) return { label: 'Incomplete', kind: 'neutral' };
  const kinds = sectionStatuses.map(memoSectionStatusKind);
  if (kinds.some((k) => k === 'danger')) return { label: 'Failed', kind: 'danger' };
  if (kinds.every((k) => k === 'success' || k === 'warning')) return { label: 'Complete', kind: 'success' };
  if (kinds.some((k) => k === 'progress')) return { label: 'Generating…', kind: 'progress' };
  return { label: 'Incomplete', kind: 'neutral' };
}

/** Memo lifecycle: draft → in_review → approved → final. */
const MEMO_STATUS_KIND: Record<string, StatusKind> = {
  draft: 'neutral',
  in_review: 'warning',
  approved: 'success',
  final: 'progress',
};

export function memoStatusKind(status: unknown): StatusKind {
  if (typeof status !== 'string') return 'neutral';
  return MEMO_STATUS_KIND[status] ?? 'neutral';
}

/** Canonical deal funnel stages (lib/dealFunnel.ts) on the status register. */
const DEAL_STAGE_KIND: Record<string, StatusKind> = {
  screening: 'neutral',
  diligence: 'progress',
  ic: 'warning',
  closed: 'success',
  passed: 'neutral',
};

export function dealStageStatusKind(stage: unknown): StatusKind {
  if (typeof stage !== 'string') return 'neutral';
  return DEAL_STAGE_KIND[stage] ?? 'neutral';
}

/** Deal lifecycle rollup (prospect → active_deal → portfolio_company → exited). */
const DEAL_LIFECYCLE_KIND: Record<string, StatusKind> = {
  prospect: 'neutral',
  active_deal: 'progress',
  portfolio_company: 'success',
  exited: 'progress',
  dead: 'neutral',
};

export function dealLifecycleStatusKind(lifecycle: unknown): StatusKind {
  if (typeof lifecycle !== 'string') return 'neutral';
  return DEAL_LIFECYCLE_KIND[lifecycle] ?? 'neutral';
}

/** Human label for any snake_case status string. */
export function statusLabel(status: unknown, fallback = 'Unknown'): string {
  if (typeof status !== 'string' || status.length === 0) return fallback;
  return (
    status
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || fallback
  );
}
